var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import * as api from '@codegrade/api';
import { Maybe, Nothing } from '@codegrade/maybe';
import { AutoTestStore, SubmissionsStore } from '@/store';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
import { QualityComments } from '../quality_comments';
import { AutoTestStepResult } from './step_result';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
class PerStudentSetupOutput {
    stdout;
    stderr;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(stdout, stderr) {
        this.stdout = stdout;
        this.stderr = stderr;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    amend(other) {
        return new PerStudentSetupOutput(this.stdout.alt(other.stdout), this.stderr.alt(other.stderr));
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const RESULT_FINISHED_STATES = new Set([
    'passed',
    'failed',
    'timed_out',
    'skipped',
]);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class AutoTestSuiteResult {
    suite;
    set;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    stepResults;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(stepResults, suite, set) {
        this.suite = suite;
        this.set = set;
        this.stepResults = stepResults.map(({ step, result }, index) => result
            .map(resultData => AutoTestStepResult.fromServerData(resultData, step, index, this))
            .orDefaultLazy(() => AutoTestStepResult.makeNonStarted(step, index, this)));
        Object.freeze(this);
    }
    get achieved() {
        return utils.sumMap(this.stepResults, cur => cur.achievedPoints);
    }
    get possible() {
        return utils.sumMap(this.stepResults, cur => cur.weight);
    }
    get finished() {
        return this.stepResults.every(stepResult => stepResult.finished);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get suiteId() {
        return this.suite.id.unsafeCoerce();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get percentage() {
        return 100 * utils.safeDivide(this.achieved, this.possible, 0);
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestSuiteResult.prototype, "achieved", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestSuiteResult.prototype, "possible", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestSuiteResult.prototype, "finished", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class AutoTestSetResult {
    set;
    result;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    suiteResults;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(steps, set, result) {
        this.set = set;
        this.result = result;
        const grouped = utils.groupByIter(steps, ({ step }) => step.suite, (one, two) => utils.compare.compare(one.id, two.id));
        this.suiteResults = grouped.map(([suite, results]) => new AutoTestSuiteResult(results, suite, this));
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get setId() {
        return this.set.id;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get ownAchieved() {
        return utils.sumMap(this.suiteResults, suiteResult => suiteResult.achieved);
    }
    get achieved() {
        return this.ownAchieved + this._previousSet.mapOrDefault(utils.attr('achieved'), 0);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get ownPossible() {
        return utils.sumMap(this.suiteResults, suiteResult => suiteResult.possible);
    }
    get possible() {
        return this.ownPossible + this._previousSet.mapOrDefault(utils.attr('possible'), 0);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get percentage() {
        return 100 * utils.safeDivide(this.achieved, this.possible, 0);
    }
    get finished() {
        return (this.suiteResults.every(r => r.finished) &&
            this._previousSet.mapOrDefault(utils.attr('finished'), true));
    }
    /**
     * Returns if this set passed.
     *
     * @returns Nothing if this set isn't done running, otherwise `true` if we
     * achieved more points than the needed `stopPoints`.
     */
    get passed() {
        if (!this.finished) {
            return Maybe.empty();
        }
        const enoughPoints = this.percentage >= 100 * this.set.stopPoints;
        // We don't care about the condition that it didn't already
        // finish. Because it always should have finished if we already
        // finished.
        const passed = enoughPoints && this.previousSetPassed.join().orDefault(true);
        return Maybe.of(passed);
    }
    /**
     * Returns if the previous set passed.
     *
     * @returns Nothing if we don't have a previous set, otherwhise the `passed`
     * attribute of the previous set.
     */
    get previousSetPassed() {
        return this._previousSet.map(utils.attr('passed'));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get _previousSet() {
        const getSetResult = this.result.setResults.map(result => utils.getItem(result));
        const prevSetId = this.set.getPreviousSet().map(utils.attr('id'));
        return getSetResult.chain(getter => prevSetId.chain(getter));
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestSetResult.prototype, "achieved", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestSetResult.prototype, "possible", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestSetResult.prototype, "finished", null);
__decorate([
    CacheDecorator.cache
], AutoTestSetResult.prototype, "passed", null);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function maybeExtendedAttr(data, key) {
    if (data instanceof api.ExtendedAutoTestResult) {
        return Maybe.of(data[key]);
    }
    return Nothing;
}
// Sort the results by
// - First the non finished results.
// - Next results just before running
// - Then any failed results (or something similar).
// - Then any results waiting to be started.
// - And finally the results that finished and didn't fail.
const resultStateSortMap = Object.freeze({
    running: 1,
    running_setup: 2,
    setting_up: 3,
    failed: 4,
    skipped: 5,
    timed_out: 6,
    not_started: 7,
    passed: 8,
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export class AutoTestResult {
    id;
    submissionId;
    autoTestId;
    createdAt;
    startedAt;
    pointsAchieved;
    isFinal;
    approxWaitingBefore;
    globalSetupOutput;
    perStudentSetup;
    state;
    maybeStepResults;
    runId;
    suitesWithFiles;
    runnerStates;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _cache = new CacheDecorator();
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    qualityComments;
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(id, submissionId, autoTestId, createdAt, startedAt, pointsAchieved, isFinal, approxWaitingBefore, globalSetupOutput, perStudentSetup, qualityComments, state, maybeStepResults, runId, suitesWithFiles, runnerStates) {
        this.id = id;
        this.submissionId = submissionId;
        this.autoTestId = autoTestId;
        this.createdAt = createdAt;
        this.startedAt = startedAt;
        this.pointsAchieved = pointsAchieved;
        this.isFinal = isFinal;
        this.approxWaitingBefore = approxWaitingBefore;
        this.globalSetupOutput = globalSetupOutput;
        this.perStudentSetup = perStudentSetup;
        this.state = state;
        this.maybeStepResults = maybeStepResults;
        this.runId = runId;
        this.suitesWithFiles = suitesWithFiles;
        this.runnerStates = runnerStates;
        this.qualityComments = qualityComments.map(data => {
            if (data instanceof QualityComments) {
                return data;
            }
            return QualityComments.fromServerData(data);
        });
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get isExtended() {
        return this.maybeStepResults.isJust();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    amountRunnersInState(state) {
        return this.runnerStates.mapOrDefault(states => states.get(state) ?? 0, 0);
    }
    get setResults() {
        return this.maybeStepResults
            .map(stepResults => {
            const stepResultLookup = utils.mapToObject(stepResults, stepResult => [stepResult.autoTestStep.id, stepResult]);
            const stepWithResult = this.autoTest.steps.map(step => {
                const result = step.id
                    .map(stepId => utils.getItem(stepResultLookup, stepId))
                    .join();
                return { step, result };
            });
            const groupedSteps = utils.groupByIter(stepWithResult, ({ step }) => step.suite.set, (one, two) => utils.compare.compare(one.id, two.id));
            return groupedSteps.map(([set, steps]) => new AutoTestSetResult(steps, set, this));
        })
            .map(setResults => utils.makeLookupMapping(setResults, 'setId'));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get maybeSubmission() {
        return SubmissionsStore.getSingleSubmission()(this.submissionId);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get submission() {
        return this.maybeSubmission.extract();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get autoTest() {
        return AutoTestStore.getTest()(this.autoTestId).unsafeCoerce();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get finished() {
        return RESULT_FINISHED_STATES.has(this.state);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get failed() {
        return this.state === 'failed' || this.state === 'timed_out';
    }
    get finishedAllSets() {
        return (this.finished &&
            this.setResults.mapOrDefault(setResults => Object.values(setResults).every(r => r.finished), false));
    }
    get suiteResults() {
        const setResults = this.setResults.mapOrDefault(setResult => Object.values(setResult), []);
        const res = setResults.reduce((acc, setResult) => utils.mapToObject(setResult.suiteResults, suiteResult => [suiteResult.suiteId, suiteResult], acc), {});
        return Object.freeze(res);
    }
    get stepResults() {
        const suiteResults = Object.values(this.suiteResults);
        return suiteResults.reduce((acc, suiteResult) => utils.mapToObject(suiteResult.stepResults, stepResult => [stepResult.stepId, stepResult], acc), {});
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getStepResult(step) {
        return step.id.chain(utils.getItem(this.stepResults));
    }
    get rubricResults() {
        return utils.fromEntries(utils.filterMap(Object.values(this.suiteResults), suiteResult => suiteResult.suite.rubricRowId.map(rowId => [rowId, suiteResult])));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    amend(other) {
        return new AutoTestResult(this.id, this.submissionId, this.autoTestId, this.createdAt, this.startedAt.alt(other.startedAt), this.pointsAchieved, this.isFinal.alt(other.isFinal), this.approxWaitingBefore.alt(other.approxWaitingBefore), this.globalSetupOutput.alt(other.globalSetupOutput), this.perStudentSetup.amend(other.perStudentSetup), this.qualityComments.alt(other.qualityComments), this.state, this.maybeStepResults.alt(other.maybeStepResults), this.runId, this.suitesWithFiles.alt(other.suitesWithFiles), this.runnerStates.alt(other.runnerStates));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data, autoTestId, autoTestRunId) {
        return new AutoTestResult(data.id, data.workId, autoTestId, data.createdAt, data.startedAt, data.pointsAchieved, maybeExtendedAttr(data, 'finalResult'), maybeExtendedAttr(data, 'approxWaitingBefore').join(), maybeExtendedAttr(data, 'globalSetupOutput'), new PerStudentSetupOutput(maybeExtendedAttr(data, 'setupStdout').join(), maybeExtendedAttr(data, 'setupStderr').join()), maybeExtendedAttr(data, 'qualityComments'), data.state, maybeExtendedAttr(data, 'stepResults'), autoTestRunId, maybeExtendedAttr(data, 'suiteFiles').map(files => new Set(Object.keys(files))), maybeExtendedAttr(data, 'possibleRunners')
            .join()
            .map(runners => runners
            .map(utils.attr('state'))
            .reduce((acc, state) => {
            acc.set(state, (acc.get(state) ?? 0) + 1);
            return acc;
        }, new Map())));
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    getSortKeys() {
        return [
            resultStateSortMap[this.state],
            this.startedAt.isJust(),
            // We want the done results to be sorted latest first, but
            // the running ones we want the oldest first. It is possible
            // that `startedAt` is `Nothing` here, but because the
            // previous key already sorts on that the actual value used
            // when `startedAt` is `null` doesn't matter here.
            this.startedAt.mapOrDefault(startedAt => (this.finished ? -startedAt.valueOf() : startedAt.valueOf()), 0),
            // If all is equal show oldest first.
            this.createdAt,
            this.maybeSubmission.map(x => x.createdAt),
        ];
    }
}
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestResult.prototype, "setResults", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestResult.prototype, "finishedAllSets", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestResult.prototype, "suiteResults", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestResult.prototype, "stepResults", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestResult.prototype, "rubricResults", null);
